import { useEffect, useState } from "react";
import { Share, SquarePlus } from "lucide-react";
import { TimiBadge } from "../../ui/Mascot";

/** Chrome / Edge / 安卓上浏览器给的“安装”事件（Safari 没有，只能教用户手动添加） */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | undefined;
const listeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => { deferred = undefined; listeners.forEach((l) => l()); });
}

export function isStandalone(): boolean {
  return matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

/** installed：已经是“添加到主屏幕”打开的；prompt：浏览器可以直接安装；manual：要用户自己在浏览器菜单里添加 */
export function installState(): "installed" | "prompt" | "manual" {
  if (isStandalone()) return "installed";
  return deferred ? "prompt" : "manual";
}

/** 教用户把网页添加到主屏幕（像 App 一样全屏打开、有自己的图标） */
export function InstallHint() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const state = installState();

  return (
    <div className="install">
      <div className="row" style={{ gap: 14 }}>
        <TimiBadge size={52} />
        <div className="col" style={{ gap: 2 }}>
          <span className="t-headline">把 TimiBot 放到主屏幕</span>
          <span className="t-footnote t-2">像 App 一样全屏打开，有自己的图标，打开更快。</span>
        </div>
      </div>
      {state === "installed" ? (
        <p className="t-subheadline">已经是从主屏幕打开的了 👍</p>
      ) : state === "prompt" ? (
        <button className="primary-button" onClick={async () => { await deferred?.prompt(); deferred = undefined; force((n) => n + 1); }}>安装</button>
      ) : ios ? (
        <ol className="install-steps t-subheadline">
          <li>用 <b>Safari</b> 打开这个网页（其它浏览器不能添加）</li>
          <li>点底部的分享按钮 <Share size={16} className="inline-icon" /></li>
          <li>往下找到“<b>添加到主屏幕</b>” <SquarePlus size={16} className="inline-icon" /></li>
          <li>点右上角“添加”</li>
        </ol>
      ) : android ? (
        <ol className="install-steps t-subheadline">
          <li>点浏览器右上角的 <b>⋮</b> 菜单</li>
          <li>选“<b>添加到主屏幕</b>”或“<b>安装应用</b>”</li>
        </ol>
      ) : (
        <ol className="install-steps t-subheadline">
          <li>Chrome / Edge：点地址栏右边的“安装”图标，或者浏览器菜单里的“<b>安装 TimiBot</b>”</li>
          <li>Safari（Mac）：菜单栏 文件 → <b>添加到程序坞</b></li>
        </ol>
      )}
    </div>
  );
}
